/**
 * GuidelinesScreen — read-only browser for Eastview underwriting guidelines.
 *
 * Ported from digital_sizer (pages/Guidelines.tsx) and reworked into a
 * three-pane reading surface:
 *   • left   — searchable, program-grouped guideline list
 *   • center — themed markdown reader with a reading-progress bar + doc header
 *   • right  — sticky "On this page" TOC driven by scrollspy
 *
 * Data comes from the live digital-sizer backend (`/guidelines` list +
 * `/guidelines/:slug` detail) via guidelinesApi. Routed at /guidelines and
 * /guidelines/:slug; a bare /guidelines redirects to the first guideline so the
 * reader is never blank.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import EditOutlined from "@mui/icons-material/EditOutlined";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { guidelinesApi } from "../../api/guidelinesApi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { getRole } from "../../lib/digitalSizerHttp";
import { extractHeadings } from "./guidelineToc";
import GuidelineListPanel from "./components/GuidelineListPanel";
import GuidelineMarkdown from "../../components/markdown/GuidelineMarkdown";
import GuidelineTocPanel from "./components/GuidelineTocPanel";
import EditGuidelineDialog from "./components/EditGuidelineDialog";
import ImportGuidelineDialog from "./components/ImportGuidelineDialog";
import { teal } from "../../theme/colors";

const CAN_EDIT_ROLES = new Set(["admin", "guideline_editor"]);

function formatUpdated(value) {
  if (!value) return null;
  return formatDate(value, null, "MMM d, yyyy");
}

function CenteredState({ icon, title, description }) {
  return (
    <Flx column center sx={{ height: "100%", px: 3, textAlign: "center", color: "#94a3b8" }}>
      {icon}
      <Txt sx={{ fontSize: 16, fontWeight: 600, color: "#334155", mt: 1.5 }}>
        {title}
      </Txt>
      {description && (
        <Txt sx={{ fontSize: 13, mt: 0.5, maxWidth: 360 }}>{description}</Txt>
      )}
    </Flx>
  );
}

export default function GuidelinesScreen() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const canEdit = useMemo(() => CAN_EDIT_ROLES.has(getRole()), []);

  const scrollRef = useRef(null);
  const contentRef = useRef(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const loadList = useCallback(() => {
    setListLoading(true);
    return guidelinesApi
      .list()
      .then((data) => setList(Array.isArray(data) ? data : []))
      .catch(() => setList([]))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadDoc = useCallback((targetSlug) => {
    if (!targetSlug) {
      setDoc(null);
      return Promise.resolve();
    }
    setDocLoading(true);
    setDocError(false);
    return guidelinesApi
      .get(targetSlug)
      .then((data) => setDoc(data))
      .catch(() => {
        setDoc(null);
        setDocError(true);
      })
      .finally(() => setDocLoading(false));
  }, []);

  useEffect(() => {
    loadDoc(slug);
  }, [slug, loadDoc]);

  // Bare /guidelines → land on the first guideline.
  useEffect(() => {
    if (slug || listLoading || list.length === 0) return;
    navigate(`/guidelines/${list[0].slug}`, { replace: true });
  }, [slug, listLoading, list, navigate]);

  const onSelect = useCallback(
    (nextSlug) => navigate(`/guidelines/${nextSlug}`),
    [navigate],
  );

  const onImported = useCallback(
    (newSlug) => {
      setImportOpen(false);
      loadList();
      if (newSlug) navigate(`/guidelines/${newSlug}`);
    },
    [loadList, navigate],
  );

  const onEdited = useCallback(() => {
    setEditOpen(false);
    loadDoc(slug); // re-fetch the updated doc (version bumped server-side)
    loadList(); // version chips in the list reflect the new version
  }, [loadDoc, loadList, slug]);

  const headings = useMemo(
    () => extractHeadings(doc?.markdown || ""),
    [doc],
  );

  // ── Anchors + scrollspy ─────────────────────────────────────────────────────
  // Assign each rendered h1/h2/h3 the id from `headings` (same document order),
  // reset scroll, then observe the headings to drive the active TOC item.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return undefined;

    if (scroller.scrollTop !== 0) scroller.scrollTop = 0;
    setProgress(0);

    const els = Array.from(content.querySelectorAll("h1, h2, h3"));
    els.forEach((el, i) => {
      if (headings[i]) el.id = headings[i].id;
    });
    if (els.length === 0) {
      setActiveId(null);
      return undefined;
    }
    setActiveId(headings[0]?.id ?? null);

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // Active = first heading (document order) currently in the upper band.
        const firstVisible = els.find((el) => visible.has(el.id));
        if (firstVisible) setActiveId(firstVisible.id);
      },
      { root: scroller, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings, doc]);

  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    setProgress(max > 0 ? Math.min(1, el.scrollTop / max) : 0);
  }, []);

  const onTocSelect = useCallback((id) => {
    const content = contentRef.current;
    if (!content) return;
    const el = content.querySelector(`[id="${CSS.escape(id)}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  }, []);

  // ── Right-pane content ──────────────────────────────────────────────────────
  const slugNotFound =
    Boolean(slug) && !listLoading && !docLoading && (docError || !doc);

  let reader;
  if (docLoading) {
    reader = (
      <Flx center sx={{ height: "100%" }}>
        <CircularProgress size={26} sx={{ color: teal[700] }} />
      </Flx>
    );
  } else if (slugNotFound) {
    reader = (
      <CenteredState
        icon={<SearchOffOutlined sx={{ fontSize: 40 }} />}
        title="Guideline not found"
        description="We couldn't find a guideline matching that link. Pick one from the list on the left."
      />
    );
  } else if (!doc) {
    reader = (
      <CenteredState
        icon={<MenuBookOutlined sx={{ fontSize: 40 }} />}
        title="Select a guideline"
        description="Choose one from the left to start reading."
      />
    );
  } else {
    const updated = formatUpdated(doc.updated_at);
    reader = (
      <>
        <Box
          sx={{
            flex: "0 0 auto",
            px: { xs: 3, md: 6 },
            pt: 3,
            pb: 2,
            borderBottom: "1px solid #eef2f5",
            bgcolor: "#fff",
          }}
        >
          <Flx ac g={1} wrap sx={{ mb: 0.5 }}>
            <Chip
              label={doc.program || "RTL"}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: teal[600],
                color: "#fff",
              }}
            />
            <Chip
              label={`v${doc.version}${doc.locked ? " · locked" : ""}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11, fontWeight: 600, color: "#475569" }}
            />
            {updated && (
              <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
                Updated {updated}
              </Txt>
            )}
            <Box sx={{ flex: 1 }} />
            {canEdit && (
              <Button
                size="small"
                startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setEditOpen(true)}
                sx={{
                  textTransform: "none",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#fff",
                  bgcolor: teal[600],
                  px: 1.5,
                  "&:hover": { bgcolor: teal[700] },
                }}
              >
                Edit
              </Button>
            )}
          </Flx>
          <Txt
            sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}
          >
            {doc.title}
          </Txt>
        </Box>

        <Box
          ref={contentRef}
          sx={{ flex: "0 0 auto", px: { xs: 3, md: 6 }, py: 3, maxWidth: 920, mx: "auto", width: "100%" }}
        >
          <GuidelineMarkdown markdown={doc.markdown || ""} />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "260px 1fr",
          lg: "280px minmax(0, 1fr) 240px",
        },
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <GuidelineListPanel
        list={list}
        loading={listLoading}
        selectedSlug={slug}
        onSelect={onSelect}
        onImport={canEdit ? () => setImportOpen(true) : undefined}
      />

      {/* Center reader (scrolls) with a thin progress bar pinned to the top. */}
      <Box sx={{ position: "relative", minHeight: 0, minWidth: 0 }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: "transparent",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.round(progress * 100)}%`,
              bgcolor: "#f59e0b",
              boxShadow: "0 0 6px rgba(245,158,11,0.6)",
              borderRadius: "0 2px 2px 0",
              transition: "width 0.1s linear",
            }}
          />
        </Box>
        <Box
          ref={scrollRef}
          onScroll={onScroll}
          sx={{ height: "100%", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}
        >
          {reader}
        </Box>
      </Box>

      {/* Right TOC — hidden below lg. */}
      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          borderLeft: "1px solid #e5e7eb",
          overflowY: "auto",
          minHeight: 0,
          px: 1.5,
        }}
      >
        {doc && !docLoading && (
          <GuidelineTocPanel
            headings={headings}
            activeId={activeId}
            onSelect={onTocSelect}
          />
        )}
      </Box>

      {canEdit && (
        <ImportGuidelineDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onSaved={onImported}
        />
      )}
      {canEdit && (
        <EditGuidelineDialog
          open={editOpen}
          slug={slug}
          doc={doc}
          onClose={() => setEditOpen(false)}
          onSaved={onEdited}
        />
      )}
    </Box>
  );
}
